'use client';
import { createTheme, type ThemeOptions } from '@mui/material/styles';
import siteConfig from '@/config/site';
import { softShadow, softShadowHover } from '@/theme/tokens';

export { softShadow, softShadowHover } from '@/theme/tokens';

/**
 * Thème « papier, crayon et jardin » — voir docs/site-design.md.
 * La palette vient de config/site.ts (brand) ; ici on décline les surfaces,
 * la typographie Baloo 2 / Nunito et le langage d'interaction « gommette ».
 */

const paper = '#FBF5E9'; // fond papier crème
const paperCard = '#FDF9F0'; // carte, un ton au-dessus du fond
const ink = '#3E3A32'; // encre brun-ardoise (mine de crayon)
const inkSoft = '#6E675C'; // texte secondaire, reste ≥ 4,5:1 sur le crème
const sage = '#5E7D5A'; // vert jardin — couleur structurante
const sageDeep = '#40553E'; // hover, texte sur bande claire
const apricot = '#E08A4C'; // accent — bouton d'appel, puces alternées
const apricotDeep = '#B9682F'; // hover du bouton abricot (contraste ≥ 3:1)
const apricotSoft = '#F3D9BC'; // surface secondaire (bande contact)

const base: ThemeOptions = {
  palette: {
    mode: siteConfig.brand.mode,
    primary: { main: sage, dark: sageDeep, contrastText: '#FBF5E9' },
    secondary: { main: apricot, dark: apricotDeep, light: apricotSoft, contrastText: '#3E3A32' },
    background: { default: paper, paper: paperCard },
    text: { primary: ink, secondary: inkSoft },
    // Le « trait de crayon » : pointillé sauge à 40 %, jamais de filet gris.
    divider: 'rgba(94,125,90,0.4)',
  },
  shape: { borderRadius: 18 },
  typography: {
    fontFamily: 'var(--font-nunito), system-ui, -apple-system, sans-serif',
    fontSize: 17,
    h1: {
      fontFamily: 'var(--font-baloo), var(--font-nunito), sans-serif',
      fontWeight: 700,
      fontSize: '2.625rem', // ~42 px
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: 'var(--font-baloo), var(--font-nunito), sans-serif',
      fontWeight: 700,
      fontSize: '1.875rem', // ~30 px
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: 'var(--font-baloo), var(--font-nunito), sans-serif',
      fontWeight: 600,
      fontSize: '1.375rem', // ~22 px
      lineHeight: 1.25,
    },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 700, fontFamily: 'var(--font-nunito), sans-serif' },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999, // pilule
          paddingInline: 26,
          paddingBlock: 12,
          fontSize: '1.05rem',
          transition: 'transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
          // Langage « gommette » : le bouton se soulève de 2 px, l'ombre s'épaissit.
          '&:hover': { transform: 'translateY(-2px)', boxShadow: softShadowHover },
          '&:active': { transform: 'translateY(0)' },
        },
        containedSecondary: {
          color: '#3E3A32',
          '&:hover': { backgroundColor: apricotDeep, color: '#FBF5E9' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 18, boxShadow: softShadow, backgroundImage: 'none' },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          // Le lien se souligne d'un trait abricot qui s'étire (trait de crayon).
          backgroundImage: `linear-gradient(${apricot}, ${apricot})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '0 100%',
          backgroundSize: '0% 2px',
          transition: 'background-size 250ms ease',
          '&:hover': { backgroundSize: '100% 2px' },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
    },
  },
};

export const theme = createTheme(base);
export default theme;
